import { Check, ArrowUpRight, Timer, Footprints, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/components/Header";

const PLANS = [
  {
    id: "run",
    icon: Timer,
    name: "5K Timed Run",
    price: "99.99",
    tagline: "Chip-timed certified 3.13 mi course",
    badge: "TIMED EVENT",
    features: [
      "Chip timing with live results",
      "Age-group awards",
      "Official event t-shirt",
      "Finisher medal",
      "Food truck, music & vendors",
    ],
    featured: true,
  },
  {
    id: "run-youth",
    icon: Timer,
    name: "5K Youth · 17 & Under",
    price: "79.99",
    tagline: "Chip-timed, same certified course",
    features: [
      "Chip timing with live results",
      "Youth age-group awards",
      "Official event t-shirt",
      "Finisher medal",
      "Food truck, music & vendors",
    ],
    featured: false,
  },
  {
    id: "walk",
    icon: Footprints,
    name: "1 Mile Fun Walk",
    price: "59.99",
    tagline: "Untimed · all ages welcome",
    features: [
      "Stroller & leashed-pet friendly",
      "Official event t-shirt",
      "Finisher medal",
      "Food truck, music & vendors",
    ],
    featured: false,
  },
  {
    id: "walk-youth",
    icon: Footprints,
    name: "Youth Walk · 17 & Under",
    price: "39.99",
    tagline: "Untimed 1 mile, perfect for kids",
    features: [
      "Stroller & leashed-pet friendly",
      "Official event t-shirt",
      "Finisher medal",
      "Food truck, music & vendors",
    ],
    featured: false,
  },
  {
    id: "team5",
    icon: Users,
    name: "Team of 5",
    price: "312.49",
    tagline: "$62.50 per runner · any mix",
    badge: "SUGGESTED · SAVE 37%",
    features: [
      "Five entries, one team name",
      "Run, walk, or mix & match",
      "Team check-in lane",
      "All individual perks included",
    ],
    featured: false,
  },
  {
    id: "team10",
    icon: Users,
    name: "Team of 10",
    price: "625.00",
    tagline: "$62.50 per runner · any mix",
    badge: "BEST VALUE · SAVE 37%",
    features: [
      "Ten entries, one team name",
      "Perfect for companies & churches",
      "Team check-in lane",
      "All individual perks included",
    ],
    featured: false,
  },
];

export const Pricing = () => (
  <section id="register" data-testid="pricing-section" className="bg-[#062012] py-24 text-white md:py-32">
    <div className="mx-auto max-w-7xl px-4 md:px-8">
      <div className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <Reveal>
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-hope-bright">
              Registration
            </p>
            <h2
              data-testid="pricing-heading"
              className="font-heading text-4xl font-black leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl"
            >
              Pick Your <span className="text-hope-bright">Distance.</span>
              <br />
              Change a <span className="text-hope-red">Life.</span>
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p data-testid="pricing-mode-note" className="max-w-md text-sm leading-relaxed text-white/60 md:text-base">
            Join us Saturday, November 21 at Celebration Park in Allen, TX.
            Warm-up kicks off at 7:00 AM and the timed 5K starts at 8:00 AM
            sharp. Grab your packet earlier that week at Hope Restored Missions
            in Plano, TX.
          </p>
        </Reveal>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="pricing-cards">
        {PLANS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <div
              data-testid={`pricing-${p.id}-card`}
              className={`relative flex h-full flex-col rounded-3xl p-7 transition-all duration-300 hover:-translate-y-2 ${
                p.featured
                  ? "bg-hope-green shadow-[0_24px_60px_-20px_rgba(30,158,74,0.6)]"
                  : "border border-white/10 bg-white/5"
              }`}
            >
              {p.badge && (
                <span
                  data-testid={`pricing-${p.id}-badge`}
                  className={`absolute -top-3 left-7 rounded-full px-4 py-1.5 text-[10px] font-bold tracking-[0.15em] ${
                    p.featured ? "bg-hope-red text-white" : "bg-white text-hope-ink"
                  }`}
                >
                  {p.badge}
                </span>
              )}
              <div className="mb-6 flex items-center gap-3">
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${p.featured ? "bg-white/20" : "bg-hope-green/20 text-hope-bright"}`}>
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="font-heading text-lg font-bold leading-tight">{p.name}</h3>
              </div>
              <div className="mb-1 flex items-baseline gap-1">
                <span className="text-xl font-bold">$</span>
                <span data-testid={`pricing-${p.id}-price`} className="font-heading text-5xl font-black tracking-tighter">
                  {p.price}
                </span>
              </div>
              <p className={`mb-8 text-sm ${p.featured ? "text-white/85" : "text-white/55"}`}>{p.tagline}</p>
              <ul className="mb-10 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm ${p.featured ? "text-white" : "text-white/75"}`}>
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-white" : "text-hope-bright"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="#register-widget"
                onClick={scrollToId("#register-widget")}
                data-testid={`pricing-${p.id}-register-btn`}
                className={`group inline-flex cursor-pointer items-center justify-center gap-2 rounded-full py-4 font-heading text-base font-bold transition-all duration-300 ${
                  p.featured
                    ? "bg-white text-hope-ink hover:bg-hope-ink hover:text-white"
                    : "bg-hope-green text-white hover:bg-hope-bright hover:text-hope-ink"
                }`}
              >
                Register Now
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
